import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { PROXIES } from "@/constants/testIds";
import { t } from "@/lib/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Wifi, WifiOff, Activity } from "lucide-react";

const PROVIDERS = ["iProxy", "Astroproxy", "LTESocks", "eSIM"];
const TYPES = ["mobile", "residential", "esim"];
const GEOS = ["US", "UK"];

const emptyForm = { provider: "iProxy", type: "mobile", geo: "US", host: "", label: "", healthy: true };

export default function Proxies() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try { setList(await api.proxies()); }
    catch { toast.error("Не удалось загрузить"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      provider: p.provider || "iProxy",
      type: p.type || "mobile",
      geo: p.geo || "US",
      host: p.host || "",
      label: p.label || "",
      healthy: p.healthy ?? true,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.provider) return toast.error("Провайдер обязателен");
    try {
      if (editing) {
        await api.updateProxy(editing, form);
        toast.success(t.common.saved);
      } else {
        await api.createProxy(form);
        toast.success("Прокси добавлен");
      }
      setOpen(false);
      setForm(emptyForm);
      setEditing(null);
      load();
    } catch { toast.error(t.common.saveFailed); }
  };

  const del = async (id) => {
    if (!window.confirm(t.common.confirmDelete)) return;
    try {
      await api.deleteProxy(id);
      toast.success(t.common.deleted);
      load();
    } catch { toast.error(t.common.failed); }
  };

  const check = async (id) => {
    try {
      const doc = await api.checkProxy(id);
      toast[doc.healthy ? "success" : "error"](
        doc.healthy ? `${doc.provider} · ${t.proxies.healthy}` : `${doc.provider} · ${t.proxies.dead}`,
      );
      load();
    } catch { toast.error(t.common.failed); }
  };

  return (
    <div data-testid={PROXIES.root}>
      <PageHeader
        title={t.proxies.title}
        description={t.proxies.description}
        actions={
          <button
            data-testid={PROXIES.addBtn}
            onClick={openNew}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" /> {t.proxies.add}
          </button>
        }
      />

      {loading ? (
        <div className="text-slate-400 text-sm">{t.common.loading}</div>
      ) : list.length === 0 ? (
        <div className="card-soft p-10 text-center">
          <Wifi className="h-10 w-10 text-slate-300 mx-auto" />
          <div className="font-display text-lg mt-3 text-slate-900">{t.proxies.empty}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((p) => (
            <div key={p.id} className="card-soft p-5">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display font-semibold text-slate-900">{p.provider}</div>
                    {p.healthy ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                        <Wifi className="h-3 w-3" /> {t.proxies.healthy}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">
                        <WifiOff className="h-3 w-3" /> {t.proxies.dead}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {t.proxies.type[p.type]} · {p.geo}
                  </div>
                  {p.label && <div className="text-xs text-slate-700 mt-1">{p.label}</div>}
                  {p.host && <div className="text-[11px] font-mono text-slate-400 mt-1 truncate">{p.host}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    data-testid={PROXIES.checkBtn(p.id)}
                    onClick={() => check(p.id)}
                    className="text-slate-400 hover:text-emerald-600 transition-colors p-1"
                    title={t.proxies.check}
                  >
                    <Activity className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={PROXIES.editBtn(p.id)}
                    onClick={() => openEdit(p)}
                    className="text-slate-400 hover:text-emerald-600 transition-colors p-1"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={PROXIES.deleteBtn(p.id)}
                    onClick={() => del(p.id)}
                    className="text-slate-300 hover:text-rose-500 transition-colors p-1"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? t.proxies.edit : t.proxies.add}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.proxies.fields.provider}</Label>
              <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROVIDERS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.proxies.fields.type}</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t.proxies.type[tp]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.proxies.fields.geo}</Label>
              <Select value={form.geo} onValueChange={(v) => setForm({ ...form, geo: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GEOS.map((g) => <SelectItem key={g} value={g}>{t.geoOptions[g] || g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.proxies.fields.label}</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1" placeholder="iProxy US #1" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500">{t.proxies.fields.host}</Label>
              <Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} className="mt-1 font-mono text-xs" placeholder="us1.iproxy.local:6001" />
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid={PROXIES.saveBtn}
              onClick={submit}
              className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors"
            >
              {editing ? t.common.saveChanges : t.common.save}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
